import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';

const SkillMatchModal = ({ show, onHide, item, actionDetails }) => {
    const { t } = useTranslation('common');

    if (!show || !item) return null;

    // Extract matched skills
    let matchedSkills = [];
    if (item.MatchedSkills && Array.isArray(item.MatchedSkills) && item.MatchedSkills.length > 0) {
        matchedSkills = item.MatchedSkills;
    } else if (item.SkillName) {
        matchedSkills = [{
            SkillName: item.SkillName,
            Count: item.SkillFrequency || 1,
            TextTypeName: item.TextTypeName,
            DimensionComponentName: item.DimensionComponentName
        }];
    } else if (item.MatchReason && item.MatchReason.includes('(')) {
        // Extract skill name from "تطابق مهارة (اسم المهارة)"
        const match = item.MatchReason.match(/\((.+?)\)/);
        const extractedName = match ? match[1] : item.MatchReason;
        matchedSkills = [{
            SkillName: extractedName,
            Count: item.SkillFrequency || 1,
            TextTypeName: item.TextTypeName,
            DimensionComponentName: item.DimensionComponentName
        }];
    }

    // Extract all other skills belonging to this lesson if available
    const otherSkills = (item.Skills && Array.isArray(item.Skills))
        ? item.Skills.filter(s => s && s.SkillName && !matchedSkills.some(m => m.SkillName === s.SkillName))
        : [];

    const totalFrequency = item.SkillFrequency || (matchedSkills.reduce((acc, curr) => acc + (curr.Count || 0), 0)) || 1;
    const matchPct = item.MatchPercentage || 85;

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size="lg"
            keyboard={true}
            enforceFocus={false}
            dialogClassName="skill-match-modal"
        >
            {/* Modal Header */}
            <Modal.Header
                closeButton
                className="border-0 px-4 pt-4 pb-3"
                style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '20px 20px 0 0'
                }}
            >
                <div className="d-flex align-items-center">
                    <div
                        className="rounded-circle d-flex align-items-center justify-content-center ml-3"
                        style={{
                            width: '46px',
                            height: '46px',
                            background: 'linear-gradient(135deg, #fd7e14 0%, #d9480f 100%)',
                            color: '#ffffff',
                            fontSize: '20px',
                            boxShadow: '0 4px 12px rgba(253, 126, 20, 0.35)',
                            flexShrink: 0
                        }}
                    >
                        <i className="fas fa-lightbulb"></i>
                    </div>
                    <div>
                        <h5 className="font-weight-bold text-dark mb-0" style={{ fontSize: '1.25rem' }}>
                            {t("Pages.Search.SkillMatchTitle", "تفاصيل تطابق المهارة")}
                        </h5>
                        <small className="text-muted">
                            {item.Name ? item.Name : t("Pages.Search.SkillDetails", "عرض المهارات وتكرارها")}
                        </small>
                    </div>
                </div>
            </Modal.Header>

            {/* Modal Body */}
            <Modal.Body className="p-4" style={{ backgroundColor: '#f8fafc', maxHeight: '70vh', overflowY: 'auto' }}>
                {/* Item Info Card */}
                <div
                    className="p-3 bg-white rounded-lg shadow-sm mb-4 border"
                    style={{
                        borderRadius: '16px',
                        borderColor: '#e9ecef'
                    }}
                >
                    <div className="d-flex align-items-center justify-content-between flex-wrap">
                        <div className="d-flex align-items-center my-1">
                            <span
                                className="badge badge-pill font-weight-bold ml-2 px-3 py-2"
                                style={{
                                    backgroundColor: '#92278f15',
                                    color: '#92278f',
                                    fontSize: '0.85rem'
                                }}
                            >
                                <i className="fas fa-book-open ml-1"></i>
                                {item.TypeLabel || item.Category || item.Type || t("Pages.Search.Lesson", "محتوى تعليمي")}
                            </span>
                            <span className="font-weight-bold text-dark h6 mb-0">
                                {item.Name}
                            </span>
                        </div>

                        {/* Top Statistics Badges */}
                        <div className="d-flex align-items-center flex-wrap my-1">
                            <span
                                className="badge badge-pill font-weight-bold ml-2 px-3 py-2 shadow-xs"
                                style={{
                                    backgroundColor: '#fff3cd',
                                    color: '#b26a00',
                                    border: '1px solid #ffeeba',
                                    fontSize: '0.85rem'
                                }}
                                title="إجمالي عدد مرات تكرار المهارات في الأسئلة"
                            >
                                <i className="fas fa-repeat ml-1"></i>
                                {t("Pages.Search.TotalFrequency", "إجمالي التكرار")}: {totalFrequency} {t("Pages.Search.Times", "مرة")}
                            </span>

                            {matchPct && (
                                <span
                                    className="badge badge-pill font-weight-bold px-3 py-2 shadow-xs"
                                    style={{
                                        backgroundColor: matchPct >= 90 ? '#d4edda' : '#cce5ff',
                                        color: matchPct >= 90 ? '#155724' : '#004085',
                                        border: matchPct >= 90 ? '1px solid #c3e6cb' : '1px solid #b8daff',
                                        fontSize: '0.85rem'
                                    }}
                                >
                                    <i className="fas fa-bullseye ml-1"></i>
                                    {t("Pages.Search.MatchRate", "نسبة التطابق")}: {matchPct}%
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Section: Matched Skills */}
                <div className="mb-3">
                    <div className="d-flex align-items-center justify-content-between mb-2 px-1">
                        <h6 className="font-weight-bold text-dark mb-0" style={{ fontSize: '1rem' }}>
                            <i className="fas fa-check-circle text-success ml-2"></i>
                            {t("Pages.Search.MatchedSkillsList", "المهارات المطابقة للبحث")} ({matchedSkills.length})
                        </h6>
                        <span className="badge badge-light text-muted font-weight-normal small">
                            {t("Pages.Search.FrequencyNotice", "عدد مرات ورود المهارة في أسئلة الدرس")}
                        </span>
                    </div>

                    {matchedSkills.length > 0 ? (
                        <div className="matched-skills-list">
                            {matchedSkills.map((skill, idx) => (
                                <div
                                    key={`matched-${skill.SkillId || idx}`}
                                    className="p-3 mb-2 bg-white rounded-lg shadow-sm border transition-all"
                                    style={{
                                        borderRadius: '14px',
                                        borderRight: '4px solid #fd7e14',
                                        borderColor: '#e9ecef'
                                    }}
                                >
                                    <div className="d-flex align-items-center justify-content-between flex-wrap">
                                        <div className="d-flex align-items-center flex-grow-1 ml-2 my-1">
                                            <div
                                                className="rounded-circle d-flex align-items-center justify-content-center ml-3 flex-shrink-0"
                                                style={{
                                                    width: '36px',
                                                    height: '36px',
                                                    backgroundColor: '#fff4e6',
                                                    color: '#fd7e14',
                                                    fontSize: '16px'
                                                }}
                                            >
                                                <i className="fas fa-star"></i>
                                            </div>
                                            <div>
                                                <div className="font-weight-bold text-dark" style={{ fontSize: '1rem', lineHeight: 1.4 }}>
                                                    {skill.SkillName || item.Name}
                                                </div>
                                                {/* Meta labels (TextType, Dimension) */}
                                                <div className="d-flex flex-wrap align-items-center mt-1">
                                                    {skill.TextTypeName && (
                                                        <span className="badge badge-light text-primary px-2 py-1 ml-1 small" style={{ backgroundColor: '#e7f5ff', borderRadius: '6px' }}>
                                                            <i className="fas fa-file-alt ml-1"></i> {skill.TextTypeName}
                                                        </span>
                                                    )}
                                                    {skill.DimensionComponentName && (
                                                        <span className="badge badge-light text-dark px-2 py-1 ml-1 small" style={{ backgroundColor: '#f1f3f5', borderRadius: '6px' }}>
                                                            <i className="fas fa-th-large ml-1"></i> {skill.DimensionComponentName}
                                                        </span>
                                                    )}
                                                    {skill.DomainName && (
                                                        <span className="badge badge-light text-info px-2 py-1 small" style={{ backgroundColor: '#e6fcf5', borderRadius: '6px' }}>
                                                            <i className="fas fa-layer-group ml-1"></i> {skill.DomainName}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Frequency Counter Badge */}
                                        <div className="my-1">
                                            <span
                                                className="badge badge-pill px-3 py-2 font-weight-bold"
                                                style={{
                                                    backgroundColor: '#fff4e6',
                                                    color: '#d9480f',
                                                    border: '1px solid #ffd8a8',
                                                    fontSize: '0.9rem'
                                                }}
                                            >
                                                <i className="fas fa-fire ml-1 text-warning"></i>
                                                {t("Pages.Search.RepeatedCount", "التكرار")}: <strong className="h6 mb-0 font-weight-bold mx-1">{skill.Count || 1}</strong> {t("Pages.Search.QuestionsCount", "سؤال / مرة")}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-4 text-center bg-white rounded-lg border text-muted">
                            <i className="fas fa-info-circle ml-1"></i>
                            {t("Pages.Search.NoSkillDetails", "تمت مطابقة المهارة في المحتوى بنجاح.")}
                        </div>
                    )}
                </div>

                {/* Section: Other Skills in this lesson if any */}
                {otherSkills.length > 0 && (
                    <div className="mt-4">
                        <h6 className="font-weight-bold text-muted mb-2 px-1 small">
                            <i className="fas fa-list-check ml-1"></i>
                            {t("Pages.Search.OtherSkillsInLesson", "مهارات إضافية في هذا الدرس")} ({otherSkills.length})
                        </h6>
                        <div className="other-skills-container" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            <div className="row no-gutters">
                                {otherSkills.map((s, i) => (
                                    <div key={`other-${s.SkillId || i}`} className="col-12 col-md-6 p-1">
                                        <div
                                            className="p-2 px-3 bg-white rounded-lg border d-flex align-items-center justify-content-between h-100"
                                            style={{ borderRadius: '10px', fontSize: '0.88rem' }}
                                        >
                                            <span className="text-dark font-weight-medium text-truncate ml-2" title={s.SkillName}>
                                                <i className="fas fa-check text-muted ml-1 small"></i>
                                                {s.SkillName}
                                            </span>
                                            {s.Count ? (
                                                <span className="badge badge-light text-muted px-2 py-1 small flex-shrink-0" style={{ borderRadius: '6px' }}>
                                                    {s.Count} {t("Pages.Search.TimesShort", "مرات")}
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </Modal.Body>

            {/* Modal Footer */}
            <Modal.Footer
                className="border-0 px-4 py-3 bg-white d-flex align-items-center justify-content-between"
                style={{ borderRadius: '0 0 20px 20px' }}
            >
                <div>
                    {actionDetails && actionDetails.url && (
                        <Link
                            to={actionDetails.url}
                            className={`btn btn-sm rounded-pill px-4 py-2 font-weight-bold shadow-sm transition-all ${actionDetails.btnClass || 'btn-primary'}`}
                            style={actionDetails.btnStyle}
                            onClick={onHide}
                        >
                            <i className={`${actionDetails.icon || 'fas fa-arrow-left'} ml-1`}></i>
                            {actionDetails.label || t("Pages.Search.ViewContent", "الانتقال إلى المحتوى")}
                        </Link>
                    )}
                </div>

                <button
                    type="button"
                    className="btn btn-secondary btn-sm rounded-pill px-4 py-2 font-weight-bold shadow-sm"
                    onClick={onHide}
                >
                    <i className="fas fa-times ml-1"></i>
                    {t('Close', 'إغلاق')}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default SkillMatchModal;
